import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { Search, Server, Play, HardDrive } from "lucide-react";
import { useDispatch } from "react-redux";
import { fetchMyServers } from "../../redux/actionCreator";
import DiskReclaimModal from "../Docker/DiskReclaimModal";
import "../Employees/employeesList.scss";

export default function MyServers() {
  const dispatch = useDispatch();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  // The server whose disk panel is open, or null. Held as the whole row
  // because the modal needs its project id, its id and a label to name in
  // the confirmations.
  const [diskFor, setDiskFor] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await dispatch(fetchMyServers());
    setLoading(false);
    if (res?.status) setServers(res.data || []);
    else setServers([]);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return servers;
    return servers.filter(
      (s) =>
        s.label?.toLowerCase().includes(k) ||
        s.host?.toLowerCase().includes(k) ||
        s.project_name?.toLowerCase().includes(k) ||
        s.username?.toLowerCase().includes(k)
    );
  }, [servers, keyword]);

  const openTerminal = (s) => {
    const qs = new URLSearchParams({
      label: `${s.project_name} · ${s.label}`,
      host: `${s.username}@${s.host}:${s.port}`,
    });
    window.open(
      `/terminal/${s.project_id}/${s._id}?${qs.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="employees-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">My Servers</h2>
          <small className="page-sub">
            Every server you can reach across all your projects.
          </small>
        </div>
        <div className="page-actions">
          <div className="search-box">
            <span className="search-icon"><Search size={16} /></span>
            <input
              className="form-control"
              placeholder="Search label, host, project, user"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
          <Button variant="light" onClick={load}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="dash-panel emp-panel">
        <div className="panel-head">
          <h3>
            Servers <span className="count-pill">{filtered.length}</span>
          </h3>
        </div>

        <div className="emp-table-wrap">
          <table className="emp-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Label</th>
                <th>Host</th>
                <th>User</th>
                <th>Auth</th>
                <th className="text-end" style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="empty-row">
                    <Spinner size="sm" animation="border" /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-row">
                    <div className="empty-state">
                      <div className="empty-art"><Server size={40} strokeWidth={1.2} /></div>
                      <h4>No servers</h4>
                      <p>
                        You don't have any servers assigned through your
                        projects yet.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s._id}>
                    <td>{s.project_name}</td>
                    <td className="emp-name">{s.label}</td>
                    <td>
                      <code>
                        {s.host}:{s.port}
                      </code>
                    </td>
                    <td>{s.username}</td>
                    <td>
                      <span className="badge bg-secondary">
                        {s.auth_type === "key" ? "Key" : "Password"}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="d-inline-flex gap-2">
                        <Button
                          size="sm"
                          variant="light"
                          onClick={() => setDiskFor(s)}
                          title="Disk usage and cleanup"
                        >
                          <HardDrive size={14} className="me-1" /> Disk
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => openTerminal(s)}
                        >
                          <Play size={14} className="me-1" /> Terminal
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {diskFor && (
        <DiskReclaimModal
          projectId={diskFor.project_id}
          serverId={diskFor._id}
          serverLabel={`${diskFor.project_name} · ${diskFor.label}`}
          onClose={() => setDiskFor(null)}
        />
      )}
    </div>
  );
}
